import React, { useMemo } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import useAvaConfig from '../hooks/useAvaConfig'
import AskTedIcon from '../icons/ask-ted'
import BubbleIcon from '../icons/bubble'
import UseCase2Icon from '../icons/use-case-2'
import { stripTrailingSlash } from '../helper/url'
import urls from '../data/url.json'

const TedMobileV2 = () => {
  const config = useAvaConfig()
  const path = window.location.pathname

  const currentData = useMemo(() => {
    const pathItem = stripTrailingSlash(path)
    const data = (urls as any)[pathItem]
    return data
  }, [path])

  const iframeRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [expand, setExpand] = useState(false)
  const [isOpenIframe, setIsOpenIframe] = useState(false)
  const [lastInteraction, setLastInteraction] = useState(Date.now())
  const [query, setQuery] = useState('')

  useEffect(() => {
    const handleMessage = (event: any) => {
      console.log('recieve event data', event)
      if (event.data === 'close-popup') {
        setIsOpen(false)
        setIsOpenIframe(false)
      } else if (event.data.includes('open-ted')) {
        const eventParts = event.data.split('|')
        if (eventParts.length > 1) {
          setQuery(eventParts[1])
        } else {
          setQuery('')
        }
        setIsOpenIframe(true)
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [config])

  const containerVariants = {
    collapsed: {
      width: 60,
      justifyContent: 'center',
      paddingRight: '0px',
      paddingLeft: '0px',
    },
    expanded: {
      width: 'calc(100vw - 3rem)',
      justifyContent: 'flex-end',
      paddingRight: '5px',
      paddingLeft: '5px',
    },
  }

  useEffect(() => {
    const idleTimeout = 5000 // 15 seconds
    const timer = setTimeout(() => {
      const currentTime = Date.now()
      if (currentTime - lastInteraction >= idleTimeout) {
        setExpand(true)
      }
    }, idleTimeout)

    return () => clearTimeout(timer)
  }, [lastInteraction])

  const getIframeStyles = () => {
    const width = window.innerWidth

    let maxHeight = '100%'
    let maxWidth = '100%'
    let widthFrame = '98%'
    let heightFrame = '-webkit-fill-available'
    let right = '0rem'
    let bottom = '2rem'
    let left = ''
    let borderRadius = '0.75rem'

    if (width >= 1660) {
      maxHeight = 'calc(100vh - 12rem)'
      maxWidth = '380px'
    } else if (width >= 1327) {
      maxHeight = 'calc(100vh - 9rem)'
      maxWidth = '380px'
    } else if (width >= 1106) {
      maxHeight = 'calc(100vh - 1rem)'
      maxWidth = '380px'
    } else if (width >= 948) {
      maxHeight = 'calc(100vh - 3rem)'
      maxWidth = '380px'
    } else if (width >= 834) {
      maxHeight = 'calc(100vh - 3rem)'
      maxWidth = '350px'
    } else if (width >= 360) {
      maxHeight = '100vh'
      maxWidth = '100%'
    }

    if (width <= 320) {
      widthFrame = '30rem'
      heightFrame = '100vh'
      right = '0rem'
      left = '0rem'
      borderRadius = '0rem'
      bottom = '0'
    } else if (width <= 375) {
      widthFrame = '30rem'
      heightFrame = '100vh'
      right = '0rem'
      left = '0rem'
      borderRadius = '0rem'
      bottom = '0'
    } else if (width <= 768) {
      widthFrame = '100vw'
      heightFrame = '100vh'
      right = '0rem'
      bottom = '0rem'
      borderRadius = '0rem'
    } else if (width < 1024) {
      widthFrame = '30rem'
      heightFrame = '100vh'
      right = '0rem'
      bottom = '0rem'
      borderRadius = '0rem'
    } else if (width >= 1024) {
      widthFrame = '30rem'
      heightFrame = '50rem'
      right = '1rem'
      bottom = '5rem'
    } else if (width >= 1440) {
      widthFrame = '30rem'
      heightFrame = '50rem'
      right = '1rem'
      bottom = '5rem'
    }

    return {
      border: 'none',
      position: 'fixed',
      flexDirection: 'column',
      justifyContent: 'space-between',
      boxShadow:
        'rgba(150, 150, 150, 0.2) 0px 10px 30px 0px, rgba(150, 150, 150, 0.2) 0px 0px 0px 1px',
      bottom,
      right,
      left,
      width: widthFrame,
      maxWidth,
      height: heightFrame,
      maxHeight,
      borderRadius,
      display: 'flex',
      zIndex: 2147483647,
      overflow: 'hidden',
      backgroundColor: '#F3F4F6',
    }
  }

  const containerStyle = {
    position: 'relative',
  }

  const mainContainerStyle = {
    position: 'relative',
    height: '60px',
    borderRadius: '9999px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    background:
      'linear-gradient(135deg, #e10043 0%, #b7367a 50%, #1ea5e3 100%)',
  }

  if (!config) return <></>

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3 },
    },
  }

  const Backdrop = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 26, 65, 60%);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    z-index: 2147483646;
  `

  const MenuContainer = styled(motion.div)`
    display: flex;
    flex-direction: column;
    position: absolute;
    right: 16px;
    bottom: 96px;
    gap: 16px;
  `

  const menuVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        when: 'beforeChildren',
        staggerChildren: 0.1,
      },
    },
  }

  const MenuButton = styled(motion.button)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;
    width: 260px;
    height: 46px;
    color: white;
    font-weight: 600;
    font-size: 18px;
    border-radius: 9999px;
    position: relative;
    overflow: hidden;
    border: none;
  `

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: 'spring', stiffness: 300, damping: 24 },
    },
  }

  const ButtonBackground = styled.div`
    position: absolute;
    inset: 0;
    z-index: 0;
    background: linear-gradient(105.51deg, #599aff 2.99%, #ff5b96 96.2%);
  `

  const ButtonText = styled.span`
    position: relative;
    z-index: 10;
    font-size: 14px;
  `

  const IconWrapper = styled.div`
    position: relative;
    z-index: 10;
  `

  const ToggleButton = styled(motion.button)`
    position: fixed;
    bottom: 20px;
    right: 16px;
    z-index: 50;
    border: none;
    background: transparent;
  `

  const toggleButtonVariants = {
    initial: { scale: 1 },
    animate: { scale: 1 },
    whileTap: { scale: 0.95 },
    whileHover: { scale: 1.05 },
  }

  return (
    <div style={containerStyle}>
      <ToggleButton
        onClick={() => setIsOpen(!isOpen)}
        variants={toggleButtonVariants}
        initial="initial"
        animate="animate"
        whileTap="whileTap"
        whileHover="whileHover"
      >
        <AnimatePresence initial={false} mode="wait">
          {isOpen ? (
            <motion.img
              key="open"
              src={`${config?.baseUrl}/revamp/magic-3.png`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              alt="Close menu"
            />
          ) : (
            <motion.img
              key="closed"
              src={`${config?.baseUrl}/revamp/magic-mobile.svg`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              alt="Open menu"
            />
          )}
        </AnimatePresence>
      </ToggleButton>

      <AnimatePresence>
        {isOpen && (
          <Backdrop
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            onClick={() => setIsOpen(false)}
          >
            <MenuContainer
              variants={menuVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {['Summarize', 'See Use Case', 'Ask Ted'].map((text, index) => (
                <MenuButton
                  key={text}
                  variants={itemVariants}
                  whileHover="hover"
                  whileTap="tap"
                  onClick={() => {
                    switch (index) {
                      case 0:
                        return window.ted.openTed(currentData.questions[0])
                      case 1:
                        return window.open(currentData.usecase)
                      case 2:
                        return window.ted.openTed()
                    }
                    setIsOpenIframe(true)
                  }}
                >
                  <ButtonBackground />
                  <ButtonText>{text}</ButtonText>
                  <IconWrapper>
                    {index === 0 && <BubbleIcon />}
                    {index === 1 && <UseCase2Icon />}
                    {index === 2 && <AskTedIcon />}
                  </IconWrapper>
                </MenuButton>
              ))}
            </MenuContainer>
          </Backdrop>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpenIframe && (
          <motion.iframe
            ref={iframeRef}
            allow="fullscreen; microphone"
            title="dify chatbot bubble window"
            id="ted-iframe"
            src={`${config.baseUrl}/ted?token=${config.token}&width=${window.innerWidth}&query=${query}`}
            style={getIframeStyles()}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

export default TedMobileV2
